import moment from "moment";
import "moment-timezone";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {setBookedTokenDetails} from "../../actions/booking";
import BuSpecificHeader from "../../components/Header/BuSpecificHeader";
import Loader from "../../components/Loader";
import "../../components/Loader/style.scss";
import TabsMenu from "../../components/TabsMenu";
import TokenCard from "../../components/TokenCard";
import useMessaging from "../../hooks/useMessaging";
import {bookQueue} from "../../services/queue/queue.service";
import {
  getCustomerSlotDetails,
  setCustomerSlotDetails,
} from "../../services/SessionStorage";
import {TokenIntermediateScreen} from "../TokenIntermediateScreen/TokenIntermediateScreen";
import "./style.scss";
import {
  proactiveErrorMapping,
  proactiveErrorMessageList,
} from "../../util/common";
import CleveronCard from "../../components/CleveronCard";
import LockerCard from "../../components/LockerCard";
import config from "../../../config";

const QueueBooked = () => {
  let customerSlotDetails = getCustomerSlotDetails();
  const [loading, setLoading] = useState(false);
  const [productDetails, setProductDetails] = useState(null);
  const navigate = useNavigate();

  const {urlLinkSource, isLoggedInUser, buName} = useSelector((state) => {
    return {
      urlLinkSource: state.globalReducer?.urlLinkSource || null,
      isLoggedInUser: state.customerReducer?.isLoggedInUser,
      buName: state.customerReducer?.customerDetails?.bu || null,
    };
  });

  const INTERMEDIATE_SCEEN_WAIT_TIME = 5; //seconds

  const sessionCurrentBookingDetails =
    customerSlotDetails.currentBookingDetails;
  const sessionSelectedStore = sessionCurrentBookingDetails.selectedStore;
  const [queueBookingDetails, setQueueBookingDetails] = useState({});
  const [showIntermediateScreen, setShowIntermediateScreen] = useState(true);
  const [showErrorScreen, setShowErrorScreen] = useState(null);

  useMessaging();
  const {t} = useTranslation();
  const currDate = useRef("test");
  const dispatch = useDispatch();

  const onQueueBooking = useCallback(async () => {
    setLoading(true);
    const stdCode = config.phone_code_map[sessionSelectedStore.country];
    let customerPhoneNumber = null;
    if (
      customerSlotDetails.customerPhoneNumberWithoutCode &&
      customerSlotDetails.customerPhoneNumberWithoutCode.length
    ) {
      customerPhoneNumber = `${stdCode}${customerSlotDetails.customerPhoneNumberWithoutCode}`;
    }
    const selectedQueueDetails = {
      userId: customerSlotDetails?.userId,
      userIdType: customerSlotDetails?.userIdType,
      serviceType: customerSlotDetails?.selectedQueueService?.serviceType,
      serviceTypeId: customerSlotDetails?.selectedQueueService?.uuid,
      queueConfigId:
        customerSlotDetails?.selectedQueueService?.queueConfigId || null,
      storeId: sessionSelectedStore?.uuid,
      source: customerSlotDetails?.source,
      customerFirstName: customerSlotDetails?.customerFirstName,
      customerLastName: customerSlotDetails?.customerLastName,
      customerPhoneNumber,
      customerEmail: customerSlotDetails?.customerEmail,
      customerLoyaltyPoints: Number(customerSlotDetails?.customerLoyaltyPoints),
    };
    try {
      let response = await bookQueue(selectedQueueDetails);
      if (response.error) {
        setLoading(false);
      } else {
        response[0].tokenUUID = response[0]?.uuid;
        dispatch(setBookedTokenDetails(response[0]));
        setQueueBookingDetails(response[0]);
        customerSlotDetails.selectedQueueService.tokenNumber =
          response[0]?.tokenNumber;
        customerSlotDetails.selectedQueueService.tokenUUID = response[0]?.uuid;
        customerSlotDetails.selectedQueueService.from = null;
        customerSlotDetails.selectedQueueService.bookingTime =
          response[0]?.bookingTime;
        const tokenHasProductDetails = response[0]?.productDetails;
        if (tokenHasProductDetails) {
          setProductDetails(tokenHasProductDetails);
        }
        setLoading(false);
        if (urlLinkSource == "web") {
          setCustomerSlotDetails({...customerSlotDetails, userId: null});
        }
      }
    } catch (err) {
      const errors = err?.response?.data?.errors;
      if (errors && errors.length > 0) {
        let errorResponseDetails = null;
        const errorCode = errors[0]?.code;
        const errorDetail = errors[0]?.detail;

        if (errorDetail) {
          try {
            errorResponseDetails = JSON.parse(errorDetail);
          } catch (e) {
            errorResponseDetails = null;
          }
        }
        const popupType = {
          CUSTOMER_HAS_ACTIVE_TOKEN: {
            tokenNumber: errorResponseDetails?.tokenNumber,
            name: "existingToken",
            clevronDetails: null,
            proactiveMessageDetails: null,
          },
          PRODUCTS_ARE_IN_CLEVERON: {
            tokenNumber: null,
            name: "clevron",
            clevronDetails: errorResponseDetails,
            proactiveMessageDetails: null,
          },
          PRODUCTS_ARE_IN_LOCKER: {
            tokenNumber: null,
            name: "locker",
            lockerDetails: errorResponseDetails,
            proactiveMessageDetails: null,
          },
        };

        const proactiveMessageDetails = {
          from: proactiveErrorMapping[errorCode],
        };

        const defaultDetails = {
          tokenNumber: null,
          clevronDetails: null,
          proactiveMessageDetails: proactiveErrorMessageList.includes(errorCode)
            ? proactiveMessageDetails
            : null,
        };

        const details = popupType[errorCode] || defaultDetails;
        setShowErrorScreen(details);
        setLoading(false);
      }
    }
  }, [
    customerSlotDetails.selectedQueueService.serviceType,
    customerSlotDetails.selectedQueueService.uuid,
    customerSlotDetails.source,
    customerSlotDetails.userId,
    sessionSelectedStore.uuid,
  ]);

  useEffect(() => {
    if (customerSlotDetails.selectedQueueService.from === "customerInfo") {
      onQueueBooking();
    } else {
      setQueueBookingDetails({
        tokenNumber: customerSlotDetails.selectedQueueService.tokenNumber,
        serviceType: customerSlotDetails.selectedQueueService.serviceType,
        serviceTypeId: customerSlotDetails.selectedQueueService.serviceTypeId,
        tokenUUID: customerSlotDetails.selectedQueueService.tokenUUID,
        moduleNumber: customerSlotDetails.selectedQueueService.numberOfModules,
        bookingTime: customerSlotDetails.selectedQueueService.bookingTime,
      });
    }
  }, [onQueueBooking]);

  useEffect(() => {
    const currTimeDate = moment.tz(moment(), customerSlotDetails.timeZone);
    currDate.current = currTimeDate.format("DD MMM, YYYY");
    setInterval(() => {
      setShowIntermediateScreen(false);
    }, INTERMEDIATE_SCEEN_WAIT_TIME * 1000);
    return () => {
      setShowIntermediateScreen(true);
    };
  }, []);

  // Manejar el botón atrás
  useEffect(() => {
    const handlePopState = () => {
      navigate("/customer/selectTurneroStoreAndType");
    };

    window.history.pushState(null, "", "/customer/selectTurneroStoreAndType");
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  const renderTokenCard = () => {
    if (showErrorScreen) {
      if (showErrorScreen.name === "clevron") {
        return (
          <CleveronCard cleveronDetails={showErrorScreen.clevronDetails} />
        );
      } else if (showErrorScreen.name === "locker") {
        return <LockerCard lockerDetails={showErrorScreen.lockerDetails} />;
      } else if (showErrorScreen.name === "existingToken") {
        return (
          <TokenCard
            productDetails={productDetails}
            token={showErrorScreen.tokenNumber}
            serviceType={customerSlotDetails?.selectedQueueService?.serviceType}
            storeName={
              customerSlotDetails.currentBookingDetails.selectedStore.name
            }
          />
        );
      }
    }

    return (
      <TokenCard
        productDetails={productDetails}
        token={queueBookingDetails.tokenNumber}
        serviceType={queueBookingDetails.serviceType}
        storeName={customerSlotDetails.currentBookingDetails.selectedStore.name}
      />
    );
  };

  return loading ? (
    <div className="loader-container">
      <Loader />
    </div>
  ) : (
    <>
      {showIntermediateScreen ? (
        <TokenIntermediateScreen />
      ) : (
        <>
          {urlLinkSource === "web" ? <BuSpecificHeader /> : null}
          {isLoggedInUser && <TabsMenu path="displayTurnero" />}
          <div className="queuebooked">{renderTokenCard()}</div>
          <div
            className={`qb-btn-container qb-bu-${
              buName === "Falabella.com" ? "falabellacom" : buName
            }`}
          >
            <button
              onClick={() => {
                setCustomerSlotDetails({...customerSlotDetails, userId: null});
                navigate("/customer/selectTurneroStoreAndType");
              }}
              className="w-full max-w-[360px] h-[56px] border-[1.5px] border-[#343E49] bg-white text-[#343E49] text-base font-semibold rounded-full hover:bg-[#343E49] hover:text-white transition-colors"
            >
              {t("requestAnothershift")}
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default QueueBooked;
